import type {
  BookExportMode,
  BookAssemblySourceChapter,
  OrderedAssemblyItem,
  OrderedAssemblySourceKind,
  SideStoryPlacementChoice,
  SideStoryPlacementKind,
} from "@/features/book-assembly/book-assembly.types";

function sortUniqueChapterIds(
  chapters: BookAssemblySourceChapter[],
  selectedChapterIds: string[],
) {
  const selectedIds = new Set(selectedChapterIds);

  return chapters
    .filter((chapter) => selectedIds.has(chapter.id))
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((chapter) => chapter.id);
}

function buildChapterItem(
  chapter: BookAssemblySourceChapter,
): OrderedAssemblyItem {
  return {
    appendix: false,
    locked: chapter.isOpening || chapter.isClosing,
    sourceId: chapter.id,
    sourceKind: "chapter",
    title: chapter.title,
  };
}

function buildSideStoryItem(input: {
  appendix: boolean;
  storyId: string;
  title: string;
}): OrderedAssemblyItem {
  return {
    appendix: input.appendix,
    locked: false,
    sourceId: input.storyId,
    sourceKind: "side_story",
    title: input.title,
  };
}

export function previewBookAssemblyTocLabel(item: OrderedAssemblyItem) {
  if (item.sourceKind === "chapter") {
    return item.title;
  }

  return item.appendix ? `${item.title} (Appendix)` : item.title;
}

export function createOrderedItemsFromSelections(input: {
  chapters: BookAssemblySourceChapter[];
  selectedChapterIds: string[];
  sideStories: Array<{
    id: string;
    title: string;
  }>;
  placements: SideStoryPlacementChoice[];
}): OrderedAssemblyItem[] {
  const chapterIds = sortUniqueChapterIds(input.chapters, input.selectedChapterIds);
  const chapterById = new Map(input.chapters.map((chapter) => [chapter.id, chapter]));
  const sideStoryById = new Map(input.sideStories.map((story) => [story.id, story]));
  const includedPlacements = input.placements.filter((placement) => placement.included);
  const beforeByChapterId = new Map<string, SideStoryPlacementChoice[]>();
  const afterByChapterId = new Map<string, SideStoryPlacementChoice[]>();
  const appendixItems: SideStoryPlacementChoice[] = [];

  for (const placement of includedPlacements) {
    if (placement.placementKind === "appendix" || !placement.anchorChapterId) {
      appendixItems.push(placement);
      continue;
    }

    const targetMap =
      placement.placementKind === "before-chapter" ? beforeByChapterId : afterByChapterId;
    const list = targetMap.get(placement.anchorChapterId) ?? [];
    list.push(placement);
    targetMap.set(placement.anchorChapterId, list);
  }

  const orderedItems: OrderedAssemblyItem[] = [];

  for (const chapterId of chapterIds) {
    const beforeItems = beforeByChapterId.get(chapterId) ?? [];
    for (const placement of beforeItems) {
      const story = sideStoryById.get(placement.storyId);
      if (!story) {
        continue;
      }

      orderedItems.push(
        buildSideStoryItem({
          appendix: false,
          storyId: story.id,
          title: story.title,
        }),
      );
    }

    const chapter = chapterById.get(chapterId);
    if (chapter) {
      orderedItems.push(buildChapterItem(chapter));
    }

    const afterItems = afterByChapterId.get(chapterId) ?? [];
    for (const placement of afterItems) {
      const story = sideStoryById.get(placement.storyId);
      if (!story) {
        continue;
      }

      orderedItems.push(
        buildSideStoryItem({
          appendix: false,
          storyId: story.id,
          title: story.title,
        }),
      );
    }
  }

  for (const placement of appendixItems) {
    const story = sideStoryById.get(placement.storyId);
    if (!story) {
      continue;
    }

    orderedItems.push(
      buildSideStoryItem({
        appendix: true,
        storyId: story.id,
        title: story.title,
      }),
    );
  }

  return dedupeOrderedItems(orderedItems);
}

function dedupeOrderedItems(items: OrderedAssemblyItem[]) {
  const seen = new Set<string>();

  return items.filter((item) => {
    const key = `${item.sourceKind}:${item.sourceId}`;
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

function clampIndex(index: number, length: number) {
  return Math.max(0, Math.min(length - 1, index));
}

export function moveOrderedItem(
  items: OrderedAssemblyItem[],
  sourceId: string,
  direction: "up" | "down",
) {
  const currentIndex = items.findIndex((item) => item.sourceId === sourceId);
  if (currentIndex < 0) {
    return items;
  }

  const item = items[currentIndex];
  if (item.sourceKind !== "side_story") {
    return items;
  }

  const nextIndex = clampIndex(
    direction === "up" ? currentIndex - 1 : currentIndex + 1,
    items.length,
  );

  if (nextIndex === currentIndex) {
    return items;
  }

  return reorderOrderedItems(items, item.sourceId, items[nextIndex]!.sourceId);
}

export function reorderOrderedItems(
  items: OrderedAssemblyItem[],
  activeId: string,
  targetId: string,
) {
  const sourceIndex = items.findIndex((item) => item.sourceId === activeId);
  const targetIndex = items.findIndex((item) => item.sourceId === targetId);

  if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) {
    return items;
  }

  const sourceItem = items[sourceIndex];
  if (!sourceItem || sourceItem.sourceKind !== "side_story") {
    return items;
  }

  const nextItems = [...items];
  nextItems.splice(sourceIndex, 1);
  nextItems.splice(targetIndex, 0, sourceItem);

  return nextItems;
}

function resolvePlacementKind(
  items: OrderedAssemblyItem[],
  sideStoryIndex: number,
  selectedChapterIds: string[],
) {
  const sideStoryItem = items[sideStoryIndex];
  if (!sideStoryItem) {
    return {
      anchorChapterId: null,
      placementKind: "appendix" as SideStoryPlacementKind,
    };
  }

  if (sideStoryItem.appendix) {
    return {
      anchorChapterId: null,
      placementKind: "appendix" as SideStoryPlacementKind,
    };
  }

  let previousChapterId: string | null = null;
  for (let index = sideStoryIndex - 1; index >= 0; index -= 1) {
    const candidate = items[index];
    if (candidate?.sourceKind === "chapter") {
      previousChapterId = candidate.sourceId;
      break;
    }
  }

  let nextChapterId: string | null = null;
  for (let index = sideStoryIndex + 1; index < items.length; index += 1) {
    const candidate = items[index];
    if (candidate?.sourceKind === "chapter") {
      nextChapterId = candidate.sourceId;
      break;
    }
  }

  if (nextChapterId) {
    const nextItem = items[sideStoryIndex + 1];
    if (nextItem?.sourceKind === "chapter") {
      return {
        anchorChapterId: nextChapterId,
        placementKind: "before-chapter" as SideStoryPlacementKind,
      };
    }
  }

  if (previousChapterId && selectedChapterIds.includes(previousChapterId)) {
    return {
      anchorChapterId: previousChapterId,
      placementKind: "after-chapter" as SideStoryPlacementKind,
    };
  }

  if (nextChapterId && selectedChapterIds.includes(nextChapterId)) {
    return {
      anchorChapterId: nextChapterId,
      placementKind: "before-chapter" as SideStoryPlacementKind,
    };
  }

  return {
    anchorChapterId: null,
    placementKind: "appendix" as SideStoryPlacementKind,
  };
}

export function syncPlacementsFromOrderedItems(input: {
  orderedItems: OrderedAssemblyItem[];
  placements: SideStoryPlacementChoice[];
  selectedChapterIds: string[];
}) {
  const includedStoryIds = new Set(
    input.orderedItems
      .filter((item) => item.sourceKind === "side_story")
      .map((item) => item.sourceId),
  );

  return input.placements.map((placement) => {
    if (!includedStoryIds.has(placement.storyId)) {
      return {
        ...placement,
        included: false,
      };
    }

    const sideStoryIndex = input.orderedItems.findIndex(
      (item) => item.sourceKind === "side_story" && item.sourceId === placement.storyId,
    );
    const nextPlacement = resolvePlacementKind(
      input.orderedItems,
      sideStoryIndex,
      input.selectedChapterIds,
    );

    return {
      ...placement,
      anchorChapterId: nextPlacement.anchorChapterId,
      included: true,
      placementKind: nextPlacement.placementKind,
    };
  });
}

export function hasValidBookAssemblyDraft(input: {
  chapters: BookAssemblySourceChapter[];
  orderedItems: OrderedAssemblyItem[];
  selectedChapterIds: string[];
}) {
  const selectedIds = new Set(input.selectedChapterIds);
  const orderedKeys = new Set(
    input.orderedItems.map((item) => `${item.sourceKind}:${item.sourceId}`),
  );

  for (const chapter of input.chapters) {
    if ((chapter.isOpening || chapter.isClosing) && !selectedIds.has(chapter.id)) {
      return false;
    }

    if (selectedIds.has(chapter.id) && !orderedKeys.has(`chapter:${chapter.id}`)) {
      return false;
    }
  }

  return input.orderedItems.length > 0;
}

export function countOrderedItemsByKind(
  items: OrderedAssemblyItem[],
  sourceKind: OrderedAssemblySourceKind,
) {
  return items.filter((item) => item.sourceKind === sourceKind).length;
}

export function collectIncludedSideStoryIds(placements: SideStoryPlacementChoice[]) {
  return placements
    .filter((placement) => placement.included)
    .map((placement) => placement.storyId);
}

export function inferBookExportModeFromSelections(input: {
  placements: SideStoryPlacementChoice[];
  selectedChapterIds: string[];
}): BookExportMode {
  const includedSideStories = collectIncludedSideStoryIds(input.placements).length;
  const includedChapters = input.selectedChapterIds.length;

  if (includedSideStories > 0 && includedChapters === 0) {
    return "side_stories_only";
  }

  if (includedSideStories > 0) {
    return "full_compendium";
  }

  return "story_only";
}
